import {useCallback} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import type {FindAdditionalData} from 'services/thesaurus/functions/types';
import type {
  ThesaurusNode,
  ThesaurusNodeWithAncestors,
} from 'services/thesaurus/types';
import {useLazyGetGrepNodeDetailsQuery} from 'services/thesaurus/thesaurusApi';

// Gathers related data for each ancestor, and sends it as related data for the current node.
export const useFindGrepAdditionalData = (): FindAdditionalData => {
  const [getGrepNodeDetails] = useLazyGetGrepNodeDetailsQuery();

  return useCallback(
    async ({
      node,
      ancestors,
    }: ThesaurusNodeWithAncestors): Promise<ThesaurusNode | undefined> => {
      const ancestorsAsRelations = [];

      for (const ancestor of ancestors) {
        if (ancestor.type && ancestor.type !== 'top_concept') {
          // These are most likely in cache already, because the user has navigated to a child node.
          const {data} = await getGrepNodeDetails(ancestor.code, true);
          const related = data?.details?.related;
          if (Array.isArray(related) && related.length > 0) {
            ancestorsAsRelations.push(...related);
          }
        }
      }

      const newNode = cloneDeep(node);
      newNode.related = [...(newNode.related ?? []), ...ancestorsAsRelations];

      return newNode;
    },
    [getGrepNodeDetails],
  );
};
